/*
 * Copyright (c) 2025-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {AfterViewInit, Component, ElementRef, OnDestroy, OnInit, signal} from '@angular/core';
import {DialogService, DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {ColDef, ColGroupDef} from 'ag-grid-community';
import {ICellEditorAngularComp} from 'ag-grid-angular';
import {Subscription} from 'rxjs';
import {ImageCellRenderComponent} from './image-cell-render.component';
import {AttachmentCellRendererComponent} from './attachment-cell-renderer.component';
import {DiagTestProbeRelationshipDialogComponent} from './diag-test-probe-relationship-dialog.component';
import {TerminalReleaseToolRelationshipDialogComponent} from './terminal-release-tool-relationship-dialog.component';
import {FieldRule} from '../../model/validation.model';
import {EsciService} from '../../service/esci.service';
import {EsciMessageService} from '../../shared/esci-message.service';
import {CommonHelperService} from '../../service/common-helper.service';
import {ValidationService} from '../../service/validation.service';

interface ComponentHistoryRow {
    changedDate: string;
    changedBy: string;
    changeType: string;
    field: string;
    oldValue: string;
    newValue: string;
    message: string;
}

@Component({
    selector: 'app-view-detail-dialog',
    template: `
        <!-- Kit Relationships section for DiagTestProbe and TerminalReleaseTool - shown at top -->
        @if ((componentName === 'DiagTestProbe' || componentName === 'TerminalReleaseTool') && !isCreate) {
          <div class="kit-relationships-section">
            <div class="section-header">
              <span class="fw-bold">Kit Relationships:</span>
              @if (kitRelationships.length === 0) {
                <span class="text-muted ms-2">None</span>
              }
              @if (kitRelationships.length > 0) {
                <span class="kit-list">
                  @for (link of kitRelationships; track link; let last = $last) {
                    <span>
                      {{getKitName(link)}}{{!last ? ', ' : ''}}
                    </span>
                  }
                </span>
              }
              <button pButton class="p-button-link p-button-sm ms-2" label="Manage"
              (click)="openKitRelationshipsDialog()"></button>
            </div>
          </div>
        }

        @if (canShowHistoryButton()) {
          <div class="detail-header-actions">
            <button pButton gmSecondary class="history-button" label="History" [disabled]="!hasHistoryTarget()" (click)="openHistoryModal()"></button>
          </div>
        }

        <app-view-detail-content
          [cols]="cols"
          [hasAdminPermission]="hasAdminPermission"
          [hasPurchAdminPermission]="hasPurchAdminPermission"
          [hasTierNAdminPermission]="hasTierNAdminPermission"
          [fieldRulesByFieldName]="fieldRulesByFieldName"
          [rowData]="rowData"
          [originalRowData]="originalRowData"
          [updatedRowData]="updatedRowData"
          (dataChange)="onDetailDataChange($event)"
          [editorRefs]="editorRefs"
          >
        </app-view-detail-content>

        <div class="flex justify-content-end gap-2 mt-2 modal-footer">
          @if (hasAdminPermission && !isCreate) {
            <button
              gmPrimary pButton label="Save {{componentName}}"
            [disabled]="hasValidationErrors"
            (click)="onSaveClick()"></button>
          }
          @if (hasAdminPermission && !isCreate) {
            <button
              gmSecondary pButton label="Apply"
            (click)="onApplyClick()"></button>
          }
          @if (hasAdminPermission && isCreate) {
            <button
              gmPrimary pButton label="Create {{componentName}}"
            [disabled]="hasValidationErrors"
            (click)="onSaveClick()"></button>
          }
          @if (isPurchAdminOnly && hasPPCO && !isCreate) {
            <button
              gmPrimary pButton label="Save PPCO"
            [disabled]="hasValidationErrors"
            (click)="onSavePPCOOnlyClick()"></button>
          }
          @if (isPurchAdminOnly && hasPPCO && !isCreate) {
            <button
              gmSecondary pButton label="Revert PPCO"
            (click)="onRevertPPCOOnlyClick()"></button>
          }
          @if (isTierNAdminOnly && hasTierNResilient && !isCreate) {
            <button
              gmPrimary pButton label="Save Tier-N Resilient"
            (click)="onSaveTierNResilientOnlyClick()"></button>
          }
          @if (isTierNAdminOnly && hasTierNResilient && !isCreate) {
            <button
              gmSecondary pButton label="Revert Tier-N Resilient"
            (click)="onRevertTierNResilientOnlyClick()"></button>
          }
          <button pButton gmSecondary label="Cancel" (click)="onCancelClick()"></button>
        </div>

        <p-dialog [(visible)]="historyModalVisible"
                  [modal]="true"
                  [style]="{ width: '80vw', maxWidth: '1200px' }"
                  [draggable]="false"
                  [resizable]="false"
                  [dismissableMask]="true"
                  [header]="componentName + ' ' + rowData?.id + ' History'">
          @if (historyLoading) {
            <div class="history-state">Loading history...</div>
          } @else if (!historyRows.length) {
            <div class="history-state">No history found for this component.</div>
          } @else {
            <p-table styleClass="history-table" [value]="historyRows" [scrollable]="true"
                     scrollHeight="55vh" [tableStyle]="{'min-width': '60rem'}">
              <ng-template pTemplate="header">
                <tr>
                  <th>Changed Date</th>
                  <th>Changed By</th>
                  <th>Type</th>
                  <th>Field</th>
                  <th>Old Value</th>
                  <th>New Value</th>
                  <th>Message</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-history>
                <tr>
                  <td>{{ history.changedDate }}</td>
                  <td>{{ history.changedBy }}</td>
                  <td>{{ history.changeType }}</td>
                  <td>{{ history.field }}</td>
                  <td>{{ history.oldValue }}</td>
                  <td>{{ history.newValue }}</td>
                  <td>{{ history.message }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </p-dialog>
        `,
    styleUrls: [`./view-detail-dialog.component.scss`],
    standalone: false
})
export class ViewDetailDialogComponent implements OnInit, AfterViewInit, OnDestroy {
    rowData: any;
    updatedRowData: any;
    originalRowData: any;
    hasAdminPermission = false;
    hasPurchAdminPermission = false;
    hasTierNAdminPermission = false;
    isPurchAdminOnly = false; // True if user has PURCH_ADMIN but not component admin
    isTierNAdminOnly = false; // True if user has COMPONENT_TIER_N_ADMIN but not component admin
    isCreate = false;
    fieldRulesByFieldName: Map<string, FieldRule> = new Map<string, FieldRule>();
    cols: (ColDef | ColGroupDef)[];
    componentName: string;
    onApply: (newData: any) => void;
    onCancel: () => void;
    onSave: (newData: any) => void;
    onSavePPCOOnly: (newData: any) => void;
    onRevertPPCOOnly: () => void;
    onSaveTierNResilientOnly: (newData: any) => void;
    onRevertTierNResilientOnly: () => void;
    // The cancelOnClose is used to determine when the user has clicked on the 'X' to close the modal.
    // We subscribe to the onClose event of the dialogRef. All the button handlers set 'cancelOnClose' to false,
    // so that the onClose event does not trigger the onCancel function.
    cancelOnClose = true;

    // These editors are populated by the view-detail-content component, keyed by colId when present
    // (multiple derived columns may share one field) and otherwise by field.
    editorRefs: { [field: string]: ICellEditorAngularComp } = {};

    // Kit relationships for DiagTestProbe
    kitRelationships: any[] = [];

    // PPCO support - track if component type has PPCO
    hasPPCO = false;
    // Tier-N Resilient support - track if component type has TierNResilient
    hasTierNResilient = false;
    private readonly historyModalVisibleSignal = signal(false);
    private readonly historyLoadingSignal = signal(false);
    private readonly historyRowsSignal = signal<ComponentHistoryRow[]>([]);
    private historySubscription: Subscription | null = null;
    hasValidationErrors = false;
    private headerMountTimer: ReturnType<typeof setTimeout> | null = null;
    private headerMountAttempts = 0;
    // Prime DynamicDialog renders its header wrapper asynchronously; bound the retries so a dialog
    // variant that never grows a '.p-dialog-header' (e.g. a future header-less style) can't loop forever.
    private static readonly HEADER_MOUNT_MAX_ATTEMPTS = 5;
    private static readonly HEADER_MOUNT_RETRY_MS = 50;

    get historyModalVisible(): boolean {
        return this.historyModalVisibleSignal();
    }

    set historyModalVisible(visible: boolean) {
        this.historyModalVisibleSignal.set(visible);
    }

    get historyLoading(): boolean {
        return this.historyLoadingSignal();
    }

    set historyLoading(loading: boolean) {
        this.historyLoadingSignal.set(loading);
    }

    get historyRows(): ComponentHistoryRow[] {
        return this.historyRowsSignal();
    }

    set historyRows(rows: ComponentHistoryRow[]) {
        this.historyRowsSignal.set(rows);
    }

    constructor(public config: DynamicDialogConfig,
                private dialogRef: DynamicDialogRef,
                private dialogService: DialogService,
                private esciService: EsciService,
                private messageService: EsciMessageService,
                private validationService: ValidationService,
                private elementRef: ElementRef<HTMLElement>) {
        this.rowData = config.data.rowData;
        this.hasAdminPermission = config.data.hasAdminPermission;
        this.hasPurchAdminPermission = config.data.hasPurchAdminPermission || false;
        this.hasTierNAdminPermission = config.data.hasTierNAdminPermission || false;
        for (const fieldRule of config.data.fieldRules) {
            this.fieldRulesByFieldName.set(fieldRule.fieldName, fieldRule);
        }
        // Use the current row data (with any grid changes) as updatedRowData
        this.updatedRowData = structuredClone(this.rowData);
        // Use originalRowData from the grid if available (to show changes made in grid),
        // otherwise use the current rowData (for new rows or rows without changes)
        this.originalRowData = structuredClone(this.rowData.originalRowData || this.rowData);
        this.cols = config.data.cols;
        this.componentName = config.data.component;
        this.isCreate = !!config.data.isCreate;
        this.onSave = config.data.onSave;
        this.onApply = config.data.onApply;
        this.onCancel = config.data.onCancel;
        this.onSavePPCOOnly = config.data.onSavePPCOOnly;
        this.onRevertPPCOOnly = config.data.onRevertPPCOOnly;
        this.onSaveTierNResilientOnly = config.data.onSaveTierNResilientOnly;
        this.onRevertTierNResilientOnly = config.data.onRevertTierNResilientOnly;
        this.cancelOnClose = true;

        // Detect optional members (ppco / tierNResilient) from the column set rather than a
        // hardcoded component-name list, so adding the field to a new Component on the backend
        // automatically lights up the matching UI affordances here.
        this.hasPPCO = CommonHelperService.hasFieldInColumns(this.cols, 'ppco');
        this.hasTierNResilient = CommonHelperService.hasFieldInColumns(this.cols, 'tierNResilient');

        // Determine if user is PURCH_ADMIN only (has PURCH_ADMIN but not component admin)
        this.isPurchAdminOnly = this.hasPurchAdminPermission && !this.hasAdminPermission;
        // Determine if user is COMPONENT_TIER_N_ADMIN only (has tier-n admin but not component admin)
        this.isTierNAdminOnly = this.hasTierNAdminPermission && !this.hasAdminPermission;
        this.recomputeHasValidationErrors();

        // Listen for the dialog close event
        this.dialogRef.onClose.subscribe(() => {
            this.onClose();
        });
    }

    ngOnInit(): void {
        if ((this.componentName === 'DiagTestProbe' || this.componentName === 'TerminalReleaseTool')
            && this.rowData?.id && this.rowData.id !== -1) {
            this.loadKitRelationships();
        }
    }

    ngAfterViewInit(): void {
        // Prime DynamicDialog renders header wrappers outside this component template.
        // Move the existing History action node into that real header container.
        this.headerMountAttempts = 0;
        this.headerMountTimer = setTimeout(() => this.attemptMountHistoryButtonInDialogHeader());
    }

    ngOnDestroy(): void {
        if (this.headerMountTimer !== null) {
            clearTimeout(this.headerMountTimer);
            this.headerMountTimer = null;
        }
        // Avoid a slow history response mutating state on a component instance that has already
        // been destroyed (the dialog was closed before the request completed).
        this.historySubscription?.unsubscribe();
        this.historySubscription = null;
    }

    loadKitRelationships(): void {
        if (this.componentName === 'DiagTestProbe') {
            this.esciService.getDiagTestProbeLinks(this.rowData.id).subscribe({
                next: (response: any) => {
                    this.kitRelationships = response?.results || [];
                },
                error: (err: any) => {
                    console.error('Error loading kit relationships:', err);
                }
            });
        } else if (this.componentName === 'TerminalReleaseTool') {
            this.esciService.getTerminalReleaseToolLinks(this.rowData.id).subscribe({
                next: (response: any) => {
                    this.kitRelationships = response?.results || [];
                },
                error: (err: any) => {
                    console.error('Error loading kit relationships:', err);
                }
            });
        }
    }

    getKitName(link: any): string {
        if (this.componentName === 'DiagTestProbe') {
            return link.diagTestProbeKit?.diagTestProbeKit || '';
        } else if (this.componentName === 'TerminalReleaseTool') {
            return link.terminalReleaseToolKit?.terminalReleaseToolKit || '';
        }
        return '';
    }

    openKitRelationshipsDialog(): void {
        let dialogComponent: any;
        let headerText: string;
        let dialogData: any;

        if (this.componentName === 'DiagTestProbe') {
            dialogComponent = DiagTestProbeRelationshipDialogComponent;
            headerText = 'Diag Test Probe Kit Relationships';
            dialogData = {
                diagTestProbe: this.rowData,
                hasAdminPermission: this.hasAdminPermission
            };
        } else if (this.componentName === 'TerminalReleaseTool') {
            dialogComponent = TerminalReleaseToolRelationshipDialogComponent;
            headerText = 'Terminal Release Tool Kit Relationships';
            dialogData = {
                componentData: this.rowData,
                hasAdminPermission: this.hasAdminPermission
            };
        } else {
            return;
        }

        this.dialogService.open(dialogComponent, {
            header: headerText,
            width: '500px',
            data: dialogData
        }).onClose.subscribe(() => {
            // Reload the relationships when the dialog closes
            this.loadKitRelationships();
        });
    }

    onSaveClick() {
        if (this.hasValidationErrors) {
            return;
        }
        this.updateDataAndCloseDialog();
        // Save the data on the server
        this.onSave(this.rowData);
    }
    onApplyClick() {
        this.updateDataAndCloseDialog();
        this.onApply(this.rowData);
    }
    onSavePPCOOnlyClick() {
        if (this.hasValidationErrors) {
            return;
        }
        this.updateDataAndCloseDialog();
        // Save only PPCO data via the PPCO-only endpoint
        this.onSavePPCOOnly?.(this.rowData);
    }
    onRevertPPCOOnlyClick() {
        this.cancelOnClose = false;
        this.dialogRef.close();
        // Revert PPCO fields on the live grid row (grid callback handles field restoration and refresh)
        this.onRevertPPCOOnly?.();
    }
    onSaveTierNResilientOnlyClick() {
        this.updateDataAndCloseDialog();
        // Save only Tier-N Resilient data via the Tier-N-only endpoint
        this.onSaveTierNResilientOnly?.(this.rowData);
    }
    onRevertTierNResilientOnlyClick() {
        this.cancelOnClose = false;
        this.dialogRef.close();
        // Revert Tier-N Resilient fields on the live grid row (grid callback handles field restoration and refresh)
        this.onRevertTierNResilientOnly?.();
    }
    onCancelClick() {
        this.dialogRef.close();
    }

    canShowHistoryButton(): boolean {
        return !this.isCreate;
    }

    hasHistoryTarget(): boolean {
        return this.getComponentIdForHistory() !== null;
    }

    openHistoryModal(): void {
        const componentId = this.getComponentIdForHistory();
        if (!this.componentName || componentId === null) {
            this.messageService.processError('Unable to load history for this component.');
            return;
        }
        // Cancel any in-flight request from a previous invocation (e.g. rapid re-opens) before starting a new one.
        this.historySubscription?.unsubscribe();
        this.historyModalVisible = true;
        this.historyLoading = true;
        this.historyRows = [];
        this.historySubscription = this.esciService.getHistoryByComponentId(this.componentName, componentId).subscribe({
            next: (historyRows: any[]) => {
                this.historyRows = this.mapHistoryRows(historyRows);
                this.historyLoading = false;
            },
            error: () => {
                this.historyLoading = false;
                this.messageService.processError(`Failed to load ${this.componentName} history.`);
            }
        });
    }

    onDetailDataChange(updatedData: any) {
        this.updatedRowData = updatedData;
        this.recomputeHasValidationErrors();
    }

    onClose() {
        if (this.cancelOnClose) {
            this.onCancel?.();
        }
    }

    updateDataAndCloseDialog() {
        // We get here from the 'Save' and the 'Apply' buttons,
        // so we don't want to call the onCancel function when the modal closes.
        this.cancelOnClose = false;
        for (const colOrGroup of this.cols || []) {
            this.updateColumnData(colOrGroup);
        }
        // Editors mutate updatedRowData (a clone); save uses rowData (live grid row reference).
        this.syncTransientFileFieldsToRowData();
        this.dialogRef.close();
    }

    private updateColumnData(colOrGroup: ColDef<any, any>): void {
        if ('field' in colOrGroup) {
            // If it has a 'field' field, then it's a ColDef object, not a ColGroupDef
            const col = colOrGroup as ColDef;
            if (col.colId?.endsWith('__placeholder')) {
                return;
            }
            if (col.field?.includes('[]')) {
                // Collection rows are already synchronized by ChildGridComponent through updatedRowData/rowData.
                // Reapplying flattened collection paths here can corrupt arrays.
                return;
            }
            if (col.cellEditorParams?.memberType === 'image_list') {
                // Each grouped image child edits one list inside the shared map. The editors update
                // updatedRowData through their valueSetter; synchronize the complete map only once here.
                const namedImageMap = CommonHelperService.getDeepValue(this.updatedRowData, col.field);
                this.syncRowFieldFromUpdatedData(col.field, namedImageMap);
                return;
            }
            const editor = this.editorRefs[col.colId || col.field] as any;
            let newValue: any;
            if (!editor || ['text', 'largeText', 'date', 'number', 'conglomeration'].includes(col.cellEditorParams?.memberType)) {
                newValue = CommonHelperService.getDeepValue(this.updatedRowData, col.field);
            } else if (typeof editor?.getValue === 'function') {
                newValue = editor.getValue();
                if (editor instanceof ImageCellRenderComponent ||
                    editor instanceof AttachmentCellRendererComponent) {
                    // Images and attachments are handled differently because their getValue() returns an object
                    const file = editor.getValue();
                    if (file && file.fileId !== -1) {
                        newValue = file.fileId;
                    } else {
                        newValue = null;
                    }
                    if (editor instanceof ImageCellRenderComponent) {
                        CommonHelperService.setDeepValue(this.updatedRowData, 'imageFile', file?.imageFile ?? null);
                    } else if (editor instanceof AttachmentCellRendererComponent) {
                        const editorParams = col.cellEditorParams ?? {};
                        const pendingFileProperty = editorParams.pendingFileProperty ?? 'attachmentFile';
                        const nameField = Object.prototype.hasOwnProperty.call(editorParams, 'nameField')
                                            ? (editorParams as any).nameField
                                            : 'attachmentName';
                        CommonHelperService.setDeepValue(this.updatedRowData, pendingFileProperty, file?.[pendingFileProperty] ?? null);
                        if (typeof nameField === 'string' && nameField.trim().length > 0) {
                            CommonHelperService.setDeepValue(this.updatedRowData, nameField, file?.[nameField] ?? null);
                        }
                    }
                }
            } else {
                return;
            }
            this.syncRowFieldFromUpdatedData(col.field, newValue);
        } else if ('children' in colOrGroup) {
            // If it didn't have a 'field' field, but does have a 'children' field, then it must be a ColGroupDef
            const colGroup = colOrGroup as ColGroupDef;
            const derivedIdsField = (colGroup as any)?.esciIdsFieldName;
            if (typeof derivedIdsField === 'string' && derivedIdsField.trim().length > 0) {
                const derivedNewValue = CommonHelperService.getDeepValue(this.updatedRowData, derivedIdsField);
                this.syncRowFieldFromUpdatedData(derivedIdsField, derivedNewValue);
                return;
            }
            // Recursively iterate over all the children
            for (const child of colGroup.children || []) {
                this.updateColumnData(child);
            }
        }
    }

    /** Multipart sidecars are not column field paths; copy them onto the live grid row before save. */
    private syncTransientFileFieldsToRowData(): void {
        const transientKeys = ['attachmentFile', 'attachmentName', 'validationReportFile', 'imageFile', 'imageFiles'];
        for (const key of transientKeys) {
            if (Object.prototype.hasOwnProperty.call(this.updatedRowData, key)) {
                this.rowData[key] = this.updatedRowData[key];
            }
        }
    }

    private syncRowFieldFromUpdatedData(fieldPath: string, newValue: any): void {
        const oldValue = CommonHelperService.getDeepValue(this.rowData, fieldPath);
        const valuesDiffer = JSON.stringify(oldValue ?? '') !== JSON.stringify(newValue ?? '');
        if (!valuesDiffer) {
            if (this.rowData?.changes) {
                this.rowData.changes.delete(fieldPath);
                if (this.rowData.state === 'changed' && this.rowData.changes.size === 0) {
                    this.rowData.state = 'saved';
                }
            }
            return;
        }

        CommonHelperService.setDeepValue(this.rowData, fieldPath, newValue);
        this.rowData.changes = this.rowData.changes || new Map<string, string>();
        const originalValue = CommonHelperService.getDeepValue(this.originalRowData, fieldPath);
        const originalDisplay = Array.isArray(originalValue) ? originalValue.join('; ') : String(originalValue ?? '');
        this.rowData.changes.set(fieldPath, 'Original Value: ' + originalDisplay);
        if (this.rowData.state !== 'new' && this.rowData.state !== 'deleted') {
            this.rowData.state = 'changed';
        }
    }

    private recomputeHasValidationErrors(): void {
        const row = this.updatedRowData || this.rowData;
        if (!row || this.fieldRulesByFieldName.size === 0) {
            this.hasValidationErrors = false;
            return;
        }
        this.hasValidationErrors = Array.from(this.fieldRulesByFieldName.values())
                                        .some((fieldRule) =>
                                            !!this.validationService.validateField(fieldRule, row, false)
                                        );
    }

    private mapHistoryRows(historyRows: any[] | null | undefined): ComponentHistoryRow[] {
        if (!Array.isArray(historyRows)) {
            return [];
        }
        return historyRows.map((row: any) => ({
            changedDate: this.toLocalDateTimeDisplayString(row?.createdDate ?? row?.Created_Date ?? row?.updatedDate ?? row?.Updated_Date),
            changedBy:   this.toDisplayString(row?.createdBy ?? row?.Created_By ?? row?.updatedBy ?? row?.Updated_By),
            changeType:  this.toLowerDisplayString(row?.changeType ?? row?.changeTypeAcro),
            field:       this.toDisplayString(row?.changefield),
            oldValue:    this.toDisplayString(row?.oldValue),
            newValue:    this.toDisplayString(row?.newValue),
            message:     this.toDisplayString(row?.message)
        }));
    }

    private toDisplayString(value: any): string {
        if (value === null || value === undefined) {
            return '';
        }
        return String(value);
    }

    private toLowerDisplayString(value: any): string {
        return this.toDisplayString(value).toLowerCase();
    }

    private toLocalDateTimeDisplayString(value: any): string {
        if (value === null || value === undefined || value === '') {
            return '';
        }

        const parsedDate = new Date(value);
        if (Number.isNaN(parsedDate.getTime())) {
            return this.toDisplayString(value);
        }

        const roundedToNearestSecond = new Date(Math.round(parsedDate.getTime() / 1000) * 1000);
        return roundedToNearestSecond.toLocaleString(undefined, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit'
        });
    }

    private getComponentIdForHistory(): number | null {
        const candidateIds = [this.rowData?.id, this.updatedRowData?.id, this.originalRowData?.id];
        for (const candidateId of candidateIds) {
            const parsed = Number(candidateId);
            if (Number.isFinite(parsed) && parsed > 0) {
                return parsed;
            }
        }
        return null;
    }

    /**
     * Attempts to relocate the History button into PrimeNG's dialog header, retrying a bounded number of
     * times (with a short delay between attempts) since the header container is rendered asynchronously
     * and is not guaranteed to exist by the very next tick in slower environments.
     */
    private attemptMountHistoryButtonInDialogHeader(): void {
        this.headerMountTimer = null;
        const mounted = this.mountHistoryButtonInDialogHeader();
        this.headerMountAttempts++;
        if (!mounted && this.headerMountAttempts < ViewDetailDialogComponent.HEADER_MOUNT_MAX_ATTEMPTS) {
            this.headerMountTimer = setTimeout(
                () => this.attemptMountHistoryButtonInDialogHeader(),
                ViewDetailDialogComponent.HEADER_MOUNT_RETRY_MS
            );
        }
    }

    /** Returns true once mounted (or when there is nothing to mount), false if the header isn't ready yet. */
    private mountHistoryButtonInDialogHeader(): boolean {
        if (!this.canShowHistoryButton()) {
            return true;
        }

        const host = this.elementRef.nativeElement;
        const actionContainer = host.querySelector('.detail-header-actions');
        const dialogElement = host.closest('.p-dialog');
        const headerElement = dialogElement?.querySelector('.p-dialog-header');
        if (!actionContainer || !headerElement) {
            return false;
        }

        actionContainer.classList.add('history-in-dialog-header');
        headerElement.appendChild(actionContainer);
        return true;
    }
}
